import React, { useEffect } from 'react';
import { withRouter } from 'react-router';
import CourseActions from '../../redux/actions/CourseActions';
import CuiButton from '../CuiComponents/Button';
import CuiIcon from '../CuiComponents/Icon';
import FocusTrap from 'focus-trap-react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import './Language.scss';
import useXapi from '../../Hooks/useXapi';
import { actionsToHideModal } from '../../redux/actions/AppHeaderModalActions';
import { isWhiteListedEnvironment } from '../../utility';

const Language = ({ location }) => {
  const { xApiEvent } = useXapi();
  const { availableCourseLanguages, language, isPseudo } = useSelector(
    state => ({
      availableCourseLanguages: state.CourseState.availableCourseLanguages,
      language: state.CourseState.language,
      isPseudo: state.LanguageState.isPseudo
    }),
    shallowEqual
  );

  const UI_AVAILABLE_LANGUAGES = [
    // [code] coincides with directory /public/locales/[code]/translation.json
    { label: 'English', code: 'en-US' },
    { label: 'Español', code: 'es-XL' },
    { label: 'العربية', code: 'ar-SA' },
    { label: 'Français', code: 'fr-FR' },
    { label: 'Português do Brasil', code: 'pt-BR' },
    { label: '简体中文', code: 'zh-CN' },
    { label: 'Русский', code: 'ru-RU' },
    { label: 'Українська', code: 'uk-UA' },
    { label: '日本語', code: 'ja-JP' },
    { label: 'Italiano', code: 'it-IT' },
    { label: 'Deutsch', code: 'de-DE' },
    { label: 'Polski', code: 'pl-PL' },
    { label: 'Қазақ', code: 'kk-KZ' },
    { label: 'Nederlands', code: 'nl-NL' },
    { label: '中文', code: 'zh-HK' },
    { label: 'Português de Portugal', code: 'pt-PT' },
    { label: 'Bahasa Indonesia', code: 'id-ID' },
    { label: 'Türk', code: 'tr-TR' },
    { label: 'Magyar', code: 'hu-HU' },
    { label: 'Română', code: 'ro-RO' },
    { label: 'हिन्दी', code: 'hi-IN' },
    { label: 'עברית', code: 'he-IL' },
    { label: 'ქართული', code: 'ka-GE' }
  ];

  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    // get the language direction
    const langDirection = i18n.dir();
    const htmlElement = document.getElementsByTagName('html')[0];

    // set the direction on the root <html> element
    htmlElement.setAttribute('dir', langDirection);

    // set the lang attribute on the root <html> element
    htmlElement.setAttribute('lang', i18n.language);
  });

  const bodyEle = document.getElementsByTagName('body')[0];
  // prevent background scrolling while the bookmarks modal is visible
  useEffect(() => {
    bodyEle.style.cssText =
      'overflow: hidden; height: auto; padding-right: 15px;';
    return function cleanup() {
      bodyEle.style.cssText = '';
    };
  });

  const handleLanguageChange = language => {
    i18n.changeLanguage(language.code).then(() => {
      // ensure i18n sets language before running dispatch to avoid unexpected results
      setTimeout(() => {
        dispatch(actionsToHideModal('CLOSE_MODAL'));
        dispatch({ type: 'RESET_COURSE' });
        dispatch(CourseActions.initCourse(location));

        // ensure the page scrolls to the active chunk
        window.userScrolled = false;
        // dispatch({ type: 'USER_SCROLL', userScroll: false });

        // we also need to reset the search index store so it grabs the new search_xx-XX.json
        dispatch({ type: 'RESET_SEARCH_DATA' });

        // we also need to reset the course index store to get new translated strings
        dispatch({ type: 'RESET_COURSE_INDEX' });
      });
    });
  };

  return (
    <FocusTrap>
      <div className="language-outer">
        <div className="row language-header-stuck">
          <div className="col-sm-12">
            <div>
              <div className="icon-circle-wrap">
                <CuiIcon icon="language" size="medium" />
              </div>
              <h1>{t('Language.language')}</h1>
            </div>
            <CuiButton
              id="close-btn"
              icon="close"
              onClick={() => {
                xApiEvent('Languages Modal Closed', window.location.href);
                dispatch(actionsToHideModal('HIDE_LANGUAGES'));
              }}
            />
          </div>
        </div>
        <div className="languages-wrap">
          <div className="row">
            <div className="col-sm-12">
              <h3>{t('Language.selectLanguage')}</h3>
              <ul className="languages">
                {isWhiteListedEnvironment() && (
                  <li
                    className={`language${isPseudo ? ' isSelected' : ''}`}
                    onClick={() =>
                      isPseudo
                        ? dispatch({
                            type: 'PSEUDO_LOCALIZE_OFF'
                          })
                        : dispatch({
                            type: 'PSEUDO_LOCALIZE_ON'
                          })
                    }
                  >
                    Psuedo Localize
                    {isPseudo && <CuiIcon icon="check-outline" size="small" />}
                  </li>
                )}
                {availableCourseLanguages
                  .filter(lang => {
                    const canView = lang.prod || isWhiteListedEnvironment();
                    return canView;
                  })
                  .map((courseLang, index) => {
                    const isSelected = courseLang.code === language.code;
                    return (
                      <li
                        tabIndex={0}
                        key={index}
                        className={`language${isSelected ? ' isSelected' : ''}`}
                        onClick={() => handleLanguageChange(courseLang)}
                        onKeyDown={e =>
                          e.keyCode === 13 || e.keyCode === 32
                            ? handleLanguageChange(courseLang)
                            : null
                        }
                      >
                        {UI_AVAILABLE_LANGUAGES.map(uiLang =>
                          uiLang.code === courseLang.code ? uiLang.label : null
                        )}
                        {isSelected && (
                          <CuiIcon icon="check-outline" size="small" />
                        )}
                      </li>
                    );
                  })}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </FocusTrap>
  );
};

export default withRouter(Language);
